import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';

import { RootState } from '../../reducers';
import {
  BaseRationData,
  EntryMethodsKeys,
  IngredientInRecipeCreation,
  PreviewRationCreation,
  TargetWeights,
} from './appRecipes.types';
import { MixingTypeCodes, recipeTypeItems } from '../../../config/constants';
import { Forms } from '../../form/form.selector';
import { RecipeType } from '../../entities/recipes/recipes.types';
import { getWaterId } from '../../entities/ingredients/ingredients.selector';

export const getIngredientsInRecipe = (state: RootState): IngredientInRecipeCreation[] =>
  state.application.recipes.recipeCreation.ingredientsInRecipe;

export const getRecipePayload = (state: RootState) => state.application.recipes.recipePayload;

export const getBaseRationData = (state: RootState): BaseRationData =>
  state.application.recipes.recipeCreation.baseRation;

export const getIsEditMode = (state: RootState): boolean => state.application.recipes.recipeCreation.isEditMode;

export const getSelectedIngredientInList = (state: RootState): string | null =>
  state.application.recipes.recipeCreation.selectedIngredientInList;

export const getTransitionDays = (state: RootState) => state.application.recipes.recipeCreation.transitionDays;

export const getCurrentTransitionDay = (state: RootState) =>
  state.application.recipes.recipeCreation.currentTransitionDay;

export const getTableView = (state: RootState) => state.application.recipes.recipeCreation.tableView;

export const getRecipeTypeId = (state: RootState): number => formValueSelector(Forms.recipeForm)(state, 'recipeType');

export const isRefusalRecipe = createSelector(
  [getRecipeTypeId],
  (recipeTypeId: number) => recipeTypeId === recipeTypeItems[RecipeType.RefusalSubstitute].id,
);

export const isIngredientResizeRecipe = createSelector(
  [getRecipeTypeId],
  (recipeTypeId: number) => recipeTypeId === recipeTypeItems[RecipeType.IngredientResize].id,
);

export const isBaseRecipe = createSelector(
  [getRecipeTypeId],
  (recipeTypeId: number) => recipeTypeId === recipeTypeItems[RecipeType.AddToBaseRecipe].id,
);

export const getMethod = (state: RootState): EntryMethodsKeys => state.application.recipes.recipeCreation.method;

export const getTargetWeightType = (state: RootState): TargetWeights =>
  state.application.recipes.recipeCreation.targetWeightType;

export const getTargetDryMatterWeight = (state: RootState): number[] =>
  state.application.recipes.recipeCreation.targetDryMatterWeight;

export const getTargetActualLoadWeight = (state: RootState): number[] =>
  state.application.recipes.recipeCreation.targetActualLoadWeight;

export const recipeAutoAdjWaterSelector = (state: RootState) =>
  formValueSelector(Forms.recipeForm)(state, 'autoAdjWater');

export const getIngredientsInRecipeListAreValid = createSelector(
  [getIngredientsInRecipe, isRefusalRecipe, recipeAutoAdjWaterSelector, getWaterId, getTransitionDays],
  (
    ingredientsInRecipe: IngredientInRecipeCreation[],
    isRefusal: boolean,
    autoAdjWater: boolean,
    waterId: string,
    transitionDays: number[],
  ) => {
    if (ingredientsInRecipe.length === 0) {
      return false;
    }

    const isRecipeTransition = transitionDays.length > 1;

    const regularIngredients = isRefusal ? ingredientsInRecipe.filter((x) => !x.refusal) : ingredientsInRecipe;

    const regularIngredientsValid = regularIngredients.every(
      (ing) =>
        (autoAdjWater && ing.id === waterId && ing.actualLoad.every((load) => load >= 0)) ||
        (ing.actualLoad.every((load) => (isRecipeTransition ? load >= 0 : load > 0)) &&
          (ing.mixerData.mixingTypeCode === MixingTypeCodes.none || ing.mixerData.mixingValue !== 0)),
    );

    if (isRefusal) {
      const refusalIngredients = ingredientsInRecipe.filter((x) => x.refusal);
      const refusalIngredientsValid = refusalIngredients.every((x) => x.id !== '-1');
      const refusalSubstituteValid = regularIngredients.some((x) => x.refusalSubstitute);

      return regularIngredientsValid && refusalIngredientsValid && refusalSubstituteValid;
    }

    return regularIngredientsValid;
  },
);

export const getBaseRationInRecipeIsValid = (state: RootState): boolean => {
  const { actualLoad, ration } = state.application.recipes.recipeCreation.baseRation;
  return !!ration && actualLoad > 0;
};

export const getIngredientsInRecipeListAreValidAtLeastOne = createSelector(
  [getIngredientsInRecipe],
  (ingredientsInRecipe: IngredientInRecipeCreation[]) => {
    if (ingredientsInRecipe.length === 0) {
      return false;
    }
    return ingredientsInRecipe.some((ing) => ing.actualLoad[0] > 0);
  },
);

export const hasWater = createSelector(
  [getIngredientsInRecipe, getWaterId],
  (ingredients: IngredientInRecipeCreation[], waterId: string | undefined) => {
    return ingredients.some((elem) => elem.id === waterId);
  },
);

export const hasIngredientsInRecipe = createSelector(
  [getIngredientsInRecipe],
  (ingredientsInRecipe): boolean => !!ingredientsInRecipe.length,
);

export const getPreviewRecipe = (state: RootState): PreviewRationCreation | null =>
  state.application.recipes.recipeCreation.previewRecipe;

export const getFinalDryMatterAverage = createSelector([getPreviewRecipe], (preview: PreviewRationCreation) =>
  preview ? preview.finalDryMatterPerc : 0,
);

export const getTotalActualLoadAmounts = createSelector([getPreviewRecipe], (preview: PreviewRationCreation) =>
  preview ? preview.finalAmount : 0,
);

export const getTotalDryMatterAmounts = createSelector(
  [getIngredientsInRecipe, getCurrentTransitionDay],
  (ingredientsInRecipe: IngredientInRecipeCreation[], currentTrabsitionDay: number) =>
    ingredientsInRecipe.reduce((acc, ing) => acc + ing.dryMatter[currentTrabsitionDay], 0),
);

export const getTotalDryMatterPercentage = createSelector(
  [getIngredientsInRecipe, getBaseRationData, isBaseRecipe, getCurrentTransitionDay],
  (
    ingredientsInRecipe: IngredientInRecipeCreation[],
    baseRation: BaseRationData,
    baseRecipe: boolean,
    currentTrabsitionDay: number,
  ) => {
    return (
      ingredientsInRecipe.reduce((acc, ing) => acc + ing.dryMatterTotalPercentage[currentTrabsitionDay], 0) +
      (baseRecipe ? baseRation.dryMatterTotalPercentage : 0)
    );
  },
);

export const getTotalActualLoadPercentage = createSelector(
  [getIngredientsInRecipe, getBaseRationData, isBaseRecipe, getCurrentTransitionDay],
  (
    ingredientsInRecipe: IngredientInRecipeCreation[],
    baseRation: BaseRationData,
    baseRecipe: boolean,
    currentTrabsitionDay: number,
  ) => {
    return (
      ingredientsInRecipe.reduce((acc, ing) => acc + ing.actualLoadTotalPercentage[currentTrabsitionDay], 0) +
      (baseRecipe ? baseRation.actualLoadTotalPercentage : 0)
    );
  },
);

export const isFullLoad = createSelector(
  [getTotalDryMatterPercentage, getTotalActualLoadPercentage],
  (dryMatterPercentage: number, actualLoadPercentage) => {
    return (
      Math.round(dryMatterPercentage * 10000) / 10000 === 100 &&
      Math.round(actualLoadPercentage * 10000) / 10000 === 100
    );
  },
);

export const getIngredientInRecipeByUUID = createSelector(
  [getIngredientsInRecipe, (_state: RootState, uuid: string) => uuid],
  (ingredients, uuid) => ingredients.find((x) => x.uuid === uuid),
);
